import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { Calendar, Clock, MapPin, ChevronDown, Globe, Settings, AlertCircle, Loader2, Search, Sparkles, UserRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

import { API_URL as API } from '@/lib/backendUrl';
import { trackEvent, ymGoal } from '@/lib/analytics';
import { sessionChartMatchesCabinet } from '@/lib/cabinetBirth';
import { seedNatalProfile } from '@/lib/natalProfileSeed';
import { useCitySearch } from '@/hooks/useCitySearch';
import { CITY_NOT_FOUND_TEXT } from '@/lib/cityNotFound';

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const YEARS = Array.from({ length: 127 }, (_, i) => 2026 - i);
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const MONTH_NAMES = { 1:'Январь',2:'Февраль',3:'Март',4:'Апрель',5:'Май',6:'Июнь',7:'Июль',8:'Август',9:'Сентябрь',10:'Октябрь',11:'Ноябрь',12:'Декабрь' };

// Demo: Bangkok, 09.03.2026, 07:37
const DEMO_BIRTH = { day:9, month:3, year:2026, time:'07:37', city:'Bangkok', country:'Thailand', latitude:13.7563, longitude:100.5018, timezone:'Asia/Bangkok', houseSystem:'Placidus' };

const STORAGE_KEY = 'astrofeel_natal_form';

// Последняя РАССЧИТАННАЯ карта — в пределах вкладки (sessionStorage). После
// программы и «Назад» / «На главную» главная показывает её с готовым
// расчётом, а не карту профиля; плашки ведут с ней (lib/homeHandoff). Новая
// вкладка или новый визит — как раньше: профиль, форма, демо. Пишется только
// по нажатию «РАССЧИТАТЬ»; карта привязана к аккаунту (гость — null), чтобы
// после выхода и входа другим человеком в той же вкладке не всплыла чужая.
const SESSION_CHART_KEY = 'astrofeel_last_chart';

function saveSessionChart(entry) {
  try { sessionStorage.setItem(SESSION_CHART_KEY, JSON.stringify(entry)); } catch { /* приватный режим, квота */ }
}
function loadSessionChart() {
  try {
    const raw = sessionStorage.getItem(SESSION_CHART_KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (!(v && v.params && v.chart && v.params.latitude != null)) return null;
    // Карта без времени, посчитанная до правила «без Луны» (нет time_rule), —
    // в ней ещё аспекты Луны. Не показываем: пересчитается заново.
    if (v.chart.time_unknown && !v.chart.time_rule) return null;
    return v;
  } catch { return null; }
}

// Параметры расчёта → поля формы (обратное к handleSubmit).
function formFromParams(p, prev) {
  const unknown = !!p.time_unknown;
  const [hh, mm] = String(p.time || '').split(':');
  return {
    ...prev,
    day: String(p.day), month: String(p.month), year: String(p.year),
    hour: unknown || hh === undefined ? '' : String(parseInt(hh, 10)),
    minute: unknown || mm === undefined ? '' : String(parseInt(mm, 10)),
    timeUnknown: unknown,
    city: p.city && p.city !== 'Unknown' ? p.city : '',
    country: p.country && p.country !== 'Unknown' ? p.country : '',
    latitude: p.latitude, longitude: p.longitude, timezone: p.timezone || '',
  };
}

// Время, на которое считается карта, когда часа рождения не знают. Полдень —
// середина суток, то есть наименьшая возможная ошибка по Луне (единственной,
// кто успевает сменить знак за день). Последнее слово всё равно за сервером:
// при time_unknown он ставит полдень сам, что бы ни прислал браузер.
const NOON_TIME = '12:00';

// Save form data to localStorage (for guest persistence)
function saveFormToStorage(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}
function loadFormFromStorage() {
  try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
}

const MiniSelect = ({ value, onChange, options, placeholder, formatOption, maxLength = 4, error, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [iv, setIv] = useState(value || '');
  const ref = useRef(null);
  useEffect(() => { setIv(value || ''); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Input type="text" inputMode="numeric" placeholder={placeholder} maxLength={maxLength} value={iv}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, maxLength); setIv(v); onChange(v); }}
          onFocus={() => setIsOpen(true)} onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          data-testid={testId}
          className={`h-8 bg-white border-slate-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-slate-800 text-center text-xs pr-6 ${error ? 'border-red-400' : ''}`} />
        <button type="button" onClick={() => setIsOpen(!isOpen)} tabIndex={-1}
          className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400">
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
          {options.map((opt) => (
            <button key={opt} type="button" tabIndex={-1}
              className={`w-full px-2 py-1 text-[11px] text-left hover:bg-sky-50 ${String(opt) === iv ? 'bg-sky-50 text-sky-700 font-medium' : 'text-slate-600'}`}
              onMouseDown={(e) => { e.preventDefault(); setIv(String(opt)); onChange(String(opt)); setIsOpen(false); }}>
              {formatOption ? formatOption(opt) : opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function useNatalCalculator() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(true);
  const [error, setError] = useState(null);
  const [autoLoaded, setAutoLoaded] = useState(false);
  // Счётчик ЗАКАЗАННЫХ расчётов: растёт только когда человек сам нажал
  // «РАССЧИТАТЬ» и карта пришла. Страница по нему подводит кадр к колесу на
  // телефоне. Флага chartData тут не хватает: демо-карта и восстановление
  // своей карты на загрузке главной тоже его меняют (и оба идут с demo=false),
  // а по ним прокручивать нельзя — это не заказанный результат.
  const [calcCount, setCalcCount] = useState(0);
  // Данные рождения, по которым посчитана ТЕКУЩАЯ карта, — ровно то, что
  // ушло на сервер. По ним главная считает плашку «Твой день сегодня»;
  // держим их рядом с картой, чтобы плашка не разъехалась с колесом.
  const [birthParams, setBirthParams] = useState(null);
  // Чья карта запоминается во вкладке (см. SESSION_CHART_KEY).
  const ownerRef = useRef(null);
  ownerRef.current = isAuthenticated && user ? (user.id || null) : null;

  const [formData, setFormData] = useState({
    // Имя — необязательное и первое: по нему потом здоровается кабинет
    // и первая строка каждого письма. Уезжает в localStorage вместе с
    // остальными полями и оттуда — в профиль при регистрации.
    name: '',
    day: '', month: '', year: '', hour: '', minute: '',
    // «Не знаю время рождения». Живёт ВНУТРИ formData, а не отдельным
    // состоянием: форма целиком уезжает в localStorage и оттуда же в
    // профиль при регистрации — флаг обязан ехать вместе с датой, иначе
    // на следующем экране карта снова попросит часы.
    timeUnknown: false,
    city: '', country: '', latitude: '', longitude: '', timezone: ''
  });
  const [citySearch, setCitySearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const suggestionsRef = useRef(null);
  // Подсказки города — только ответ на ТЕКУЩИЙ текст поля
  // (hooks/useCitySearch). Было: после правки подставленного из профиля
  // «Ухта, Республика Коми» поиск уходил по ещё не обновлённому значению, его
  // поздний ответ ложился поверх «Харьк», и клик по первой строке выбирал
  // Ухту. Теперь прошлые ответы отбрасываются, а пока ответа на набранное
  // нет — в списке «Ищем…». Выбранный город (из профиля, localStorage или
  // кликом) не ищется заново.
  const cityPicked = !!(formData.city && formData.latitude);
  const { suggestions: citySuggestions, isSearching } = useCitySearch(citySearch, {
    search: async (q, signal) => {
      try {
        const r = await fetch(`${API}/calculator/cities/search?q=${encodeURIComponent(q)}&limit=10`, { signal });
        const d = await r.json();
        return Array.isArray(d) ? d : [];
      } catch (e) {
        if (signal.aborted) throw e;
        return [];
      }
    },
    enabled: !cityPicked,
    minLength: 1,
    delay: 300,
  });

  // Поколение запроса. Раньше два расчёта не могли идти одновременно:
  // кнопка была заперта на время автозапуска, и это скрывало гонку.
  // Теперь форма от автозапуска не зависит, человек может нажать
  // «РАССЧИТАТЬ» пока демо-карта ещё летит — и без счётчика поздний
  // автоответ затёр бы его карту, вернув заодно isDemo и бейдж
  // «Пример карты» на его собственный расчёт. Побеждает последний
  // начатый запрос, ответы всех предыдущих молча выбрасываются.
  const reqSeq = useRef(0);

  // Подсказки по мере заполнения («Солнце в Овне: …») и колесо, которое
  // дорисовывается, пока человек вводит данные. Только на ЕГО правки:
  // подстановка из профиля, localStorage и демо-карта подсказок не дают.
  const touchedRef = useRef(false);
  const editForm = useCallback((updater) => {
    touchedRef.current = true;
    setFormData(updater);
  }, []);
  const [preview, setPreview] = useState(null);
  const previewSeq = useRef(0);

  // auto — расчёт, которого посетитель не заказывал: демо-карта при
  // первом заходе и восстановление своей карты из профиля/localStorage.
  // Такой расчёт не трогает ни isLoading, ни error, то есть не запирает
  // кнопку и не рисует в форме ошибку по запросу, который человек не
  // делал. Различать по demo нельзя: восстановление из профиля и из
  // localStorage идёт с demo=false, но остаётся автозапуском.
  const fetchChart = useCallback(async (params, demo = false, auto = false) => {
    const seq = ++reqSeq.current;
    if (!auto) {
      setIsLoading(true);
      setError(null);
    }
    // Without a timeout, a hung backend leaves the button stuck on
    // "Расчёт..." forever with no way for the user to tell it's dead.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 25000);
    try {
      const res = await fetch(`${API}/calculator/natal-chart/calculate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
        signal: controller.signal,
      });
      if (!res.ok) {
        // Read the body once as text, then try JSON. Avoids the
        // "Body is disturbed or locked" error when a fetch wrapper
        // has already consumed the response body.
        let detail = 'Ошибка расчёта';
        try {
          const txt = await res.text();
          if (txt) {
            try { detail = JSON.parse(txt).detail || detail; }
            catch (_e) { /* non-JSON body */ }
          }
        } catch (_e) { /* body already consumed by an interceptor */ }
        throw new Error(detail);
      }
      const data = await res.json();
      // Пока ответ шёл, начался более свежий расчёт — этот уже не нужен.
      if (seq !== reqSeq.current) return;
      setChartData(data);
      setBirthParams(params);
      setIsDemo(demo);
      // Цель Метрики на ФАКТ выданной карты — аналог horar_answer в
      // HoraryPage. Уходит ТОЛЬКО с пути явного нажатия «РАССЧИТАТЬ»
      // (auto === false) — там же, где уходит click_calculate с
      // calculator_type:natal. Ни демо-карта при первом заходе, ни
      // восстановление своей карты на загрузке главной (из профиля
      // вошедшего или из astrofeel_natal_form) цель не отправляют:
      // иначе она считала бы открытие главной, а не заказанный
      // результат — ровно та ошибка, из-за которой у хорара «Открыл
      // хорар» показывал 35 вопросов против 28 реальных. Проверки
      // !demo мало: восстановление идёт с demo=false.
      if (!demo && !auto) {
        ymGoal('natal_answer');
        setCalcCount((n) => n + 1);
        saveSessionChart({ params, chart: data, owner: ownerRef.current });
      }
    } catch (err) {
      if (seq !== reqSeq.current) return;
      if (!auto) {
        setError(err.name === 'AbortError'
          ? 'Сервер не отвечает. Проверьте соединение и попробуйте ещё раз.'
          : err.message);
      }
    } finally {
      clearTimeout(timeoutId);
      // Спиннер снимает только тот запрос, который его поставил и всё
      // ещё актуален: иначе оборвавшийся старый расчёт погасил бы
      // «Расчёт...» у нового, который ещё идёт.
      if (!auto && seq === reqSeq.current) setIsLoading(false);
    }
  }, []);

  // On mount: user profile > localStorage > demo
  useEffect(() => {
    if (autoLoaded) return;
    // Wait for auth to finish before deciding data source
    if (authLoading) return;

    // 0. Последняя рассчитанная в этой вкладке карта — с готовым расчётом,
    // без запроса. Только своя: гость — своя гостевая, вошедший — своя.
    const last = loadSessionChart();
    // Кабинет главнее (14.10.2026): у вошедшей с заполненными данными
    // рождения карта вкладки берётся, только если это её же данные из
    // кабинета. Изменила кабинет или считала здесь подругу — после
    // перезагрузки снова её карта по кабинету.
    const cabinetTimeUnknown = !!(isAuthenticated && user?.birth_time_unknown);
    const cabinetFull = !!(isAuthenticated && user?.birth_date && user?.birth_lat && user?.birth_lon
      && (user?.birth_time || cabinetTimeUnknown));
    if (last && (last.owner || null) === ownerRef.current
        && (!cabinetFull || sessionChartMatchesCabinet(last.params, user))) {
      setFormData((prev) => formFromParams(last.params, prev));
      setCitySearch([last.params.city, last.params.country]
        .filter((x) => x && x !== 'Unknown').join(', '));
      setChartData(last.chart);
      setBirthParams(last.params);
      setIsDemo(false);
      setAutoLoaded(true);
      return;
    }

    // 1. User with profile data
    // У профиля с неизвестным временем birth_time пустой — условие на него
    // такой профиль не пускало, и человек видел демо-карту вместо своей.
    const profileTimeUnknown = !!(isAuthenticated && user?.birth_time_unknown);
    if (isAuthenticated && user?.birth_date && user?.birth_lat && user?.birth_lon
        && (user?.birth_time || profileTimeUnknown)) {
      const [y, m, d] = user.birth_date.split('-');
      const profTime = user.birth_time || '';
      setFormData(prev => ({
        ...prev, year: y, month: String(parseInt(m)), day: String(parseInt(d)),
        hour: profTime ? String(parseInt(profTime.split(':')[0])) : '',
        minute: profTime ? String(parseInt(profTime.split(':')[1])) : '',
        timeUnknown: profileTimeUnknown,
        city: user.birth_city || '', country: '',
        latitude: user.birth_lat, longitude: user.birth_lon,
        timezone: user.birth_timezone || ''
      }));
      if (user.birth_city) setCitySearch(user.birth_city);
      fetchChart({
        day: parseInt(d), month: parseInt(m), year: parseInt(y),
        time: profileTimeUnknown ? NOON_TIME : profTime,
        time_unknown: profileTimeUnknown,
        city: user.birth_city || 'Unknown', country: 'Unknown',
        latitude: parseFloat(user.birth_lat), longitude: parseFloat(user.birth_lon),
        timezone: user.birth_timezone || undefined, houseSystem: 'Placidus'
      }, false, true);
      setAutoLoaded(true);
      return;
    }

    // 2. Saved in localStorage (guest returning)
    const saved = loadFormFromStorage();
    const fd = saved?.formData || saved;
    if (fd && fd.latitude && fd.longitude && fd.day) {
      setFormData(fd);
      if (saved?.citySearch) setCitySearch(saved.citySearch);
      const savedUnknown = !!fd.timeUnknown;
      const timeStr = savedUnknown
        ? NOON_TIME
        : `${String(fd.hour).padStart(2, '0')}:${String(fd.minute).padStart(2, '0')}`;
      fetchChart({
        day: parseInt(fd.day), month: parseInt(fd.month), year: parseInt(fd.year),
        time: timeStr, time_unknown: savedUnknown,
        city: fd.city || 'Unknown', country: fd.country || 'Unknown',
        latitude: parseFloat(fd.latitude), longitude: parseFloat(fd.longitude),
        timezone: fd.timezone || undefined, houseSystem: 'Placidus'
      }, false, true);
      setAutoLoaded(true);
      return;
    }

    // 3. Demo
    fetchChart(DEMO_BIRTH, true, true);
    setAutoLoaded(true);
  }, [isAuthenticated, user, autoLoaded, authLoading, fetchChart]);

  // Открыть карту по готовым параметрам расчёта — возврат на главную после
  // оплаты 199 ₽ (?unlock=…): купленной может быть не своя карта (мужчины,
  // подруги), а главная сама восстанавливает свою — из профиля или формы.
  // Форма заполняется теми же данными, расчёт идёт как автозапуск (без цели
  // natal_answer и без спиннера кнопки).
  const loadChart = useCallback((p) => {
    if (!p || p.latitude == null || p.longitude == null || !p.day) return;
    const unknown = !!p.time_unknown;
    const [hh, mm] = String(p.time || '').split(':');
    setFormData((prev) => ({
      ...prev,
      day: String(p.day), month: String(p.month), year: String(p.year),
      hour: unknown || hh === undefined ? '' : String(parseInt(hh, 10)),
      minute: unknown || mm === undefined ? '' : String(parseInt(mm, 10)),
      timeUnknown: unknown,
      city: p.city && p.city !== 'Unknown' ? p.city : '', country: p.country && p.country !== 'Unknown' ? p.country : '',
      latitude: p.latitude, longitude: p.longitude, timezone: p.timezone || '',
    }));
    if (p.city && p.city !== 'Unknown') setCitySearch(p.city);
    fetchChart({ ...p, time: unknown ? NOON_TIME : p.time, houseSystem: p.houseSystem || 'Placidus' }, false, true);
  }, [fetchChart]);

  useEffect(() => {
    const h = (e) => { if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleCitySelect = (city) => {
    editForm(p => ({ ...p, city: city.city, country: city.country, latitude: city.latitude, longitude: city.longitude, timezone: city.timezone }));
    setCitySearch(city.displayName || `${city.city}, ${city.country}`);
    setShowSuggestions(false);
  };

  const isFormValid = () => {
    const day = parseInt(formData.day), month = parseInt(formData.month), year = parseInt(formData.year);
    const hour = parseInt(formData.hour), minute = parseInt(formData.minute);
    const dateOk = day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2026;
    // Время неизвестно — часы и минуты не спрашиваем вовсе: ради этого
    // галочка и заведена.
    const timeOk = formData.timeUnknown
      || (!isNaN(hour) && hour >= 0 && hour <= 23 && !isNaN(minute) && minute >= 0 && minute <= 59);
    return dateOk && timeOk && formData.latitude && formData.longitude;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;
    // Приоритет 4: homepage's inline calculator — funnel step 2
    // (server.py admin_get_funnel_stats: click_calculate).
    const timeUnknown = !!formData.timeUnknown;
    trackEvent('click_calculate', { calculator_type: 'natal', time_unknown: timeUnknown });
    const timeStr = timeUnknown
      ? NOON_TIME
      : `${String(formData.hour).padStart(2, '0')}:${String(formData.minute).padStart(2, '0')}`;
    // Save to localStorage for persistence
    saveFormToStorage({ formData, citySearch });
    // …и в профиль, если вошедший считает карту впервые: дальше эти данные
    // подставятся в «Где твои деньги» и остальные программы. Пустой
    // user.birth_date — это и есть «профиля ещё нет»: /auth/me берёт его
    // из user_natal_profiles. Последнее слово всё равно за сервером
    // (only_if_absent), здесь просто не шлём заведомо лишний запрос.
    if (isAuthenticated && !user?.birth_date) seedNatalProfile({ formData, citySearch });
    await fetchChart({
      day: parseInt(formData.day), month: parseInt(formData.month), year: parseInt(formData.year),
      time: timeStr, time_unknown: timeUnknown,
      city: formData.city || 'Unknown', country: formData.country || 'Unknown',
      latitude: parseFloat(formData.latitude), longitude: parseFloat(formData.longitude),
      timezone: formData.timezone || undefined, houseSystem: 'Placidus'
    }, false);
  };

  // Частичный расчёт по заполненным полям: дата → Солнце, + время → Луна,
  // + место → Асцендент. Ответ — те же planets/angles, что у полной карты.
  const pDay = formData.day, pMonth = formData.month, pYear = formData.year;
  const pHour = formData.hour, pMinute = formData.minute, pUnknown = formData.timeUnknown;
  const pLat = formData.latitude, pLon = formData.longitude, pTz = formData.timezone;
  useEffect(() => {
    if (!touchedRef.current) return undefined;
    const day = parseInt(pDay, 10), month = parseInt(pMonth, 10), year = parseInt(pYear, 10);
    if (!(day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2026)) {
      setPreview(null);
      return undefined;
    }
    const hour = parseInt(pHour, 10), minute = parseInt(pMinute, 10);
    const timeOk = !pUnknown && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
    const placeOk = pLat !== '' && pLon !== '' && !isNaN(parseFloat(pLat)) && !isNaN(parseFloat(pLon));
    let browserTz = 'Europe/Moscow';
    try { browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || browserTz; } catch { /* old browser */ }
    const body = { day, month, year, timezone: (placeOk && pTz) || browserTz };
    if (timeOk) body.time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    if (placeOk) { body.latitude = parseFloat(pLat); body.longitude = parseFloat(pLon); }
    const seq = ++previewSeq.current;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/calculator/natal-chart/partial`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (seq === previewSeq.current) setPreview(data);
      } catch { /* подсказка — не условие расчёта */ }
    }, 400);
    return () => clearTimeout(t);
  }, [pDay, pMonth, pYear, pHour, pMinute, pUnknown, pLat, pLon, pTz]);

  const previewHints = (preview && preview.hints) || [];
  const previewChart = preview && (preview.planets || []).length ? preview : null;

  const getUserTier = () => {
    if (!isAuthenticated || !user) return 'FREE';
    const t = user.effective_tier || user.access_level || 1;
    if (t >= 3) return 'PRO';
    if (t >= 2) return 'ASTROFEEL';
    return 'FREE';
  };

  // ─── FORM ───
  const renderForm = () => (
    // id нужен якорю: помощник на главной не уводит на другую страницу, а
    // закрывается и прокручивает человека сюда, к форме расчёта.
    <form id="natal-form" onSubmit={handleSubmit} className="space-y-2" data-testid="natal-chart-form">
      <div>
        <Label className="flex items-center gap-1 text-slate-600 text-[11px] font-medium mb-1">
          <UserRound className="w-3 h-3 text-slate-400" /> Имя
        </Label>
        <Input
          type="text"
          value={formData.name}
          onChange={(e) => editForm(p => ({ ...p, name: e.target.value }))}
          placeholder="Как к тебе обращаться"
          maxLength={60}
          autoComplete="given-name"
          className="h-8 bg-white border-slate-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-slate-800 text-xs"
          data-testid="natal-name"
        />
      </div>
      <div>
        <Label className="flex items-center gap-1 text-slate-600 text-[11px] font-medium mb-1">
          <Calendar className="w-3 h-3 text-slate-400" /> Дата рождения
        </Label>
        <div className="grid grid-cols-3 gap-1.5">
          <MiniSelect value={formData.day} onChange={(v) => editForm(p => ({ ...p, day: v }))}
            options={DAYS} placeholder="День" maxLength={2} testId="natal-birth-day" />
          <MiniSelect value={formData.month} onChange={(v) => editForm(p => ({ ...p, month: v }))}
            options={MONTHS} placeholder="Мес" formatOption={(m) => `${m} ${MONTH_NAMES[m]}`} maxLength={2} testId="natal-birth-month" />
          <MiniSelect value={formData.year} onChange={(v) => editForm(p => ({ ...p, year: v }))}
            options={YEARS} placeholder="Год" maxLength={4} testId="natal-birth-year" />
        </div>
      </div>
      <div>
        <Label className="flex items-center gap-1 text-slate-600 text-[11px] font-medium mb-1">
          <Clock className="w-3 h-3 text-slate-400" /> Время рождения
        </Label>
        {/* Поля времени гаснут под галочкой — так видно, что спрашивать
            больше нечего, и человек не ищет, что ещё заполнить. */}
        <div className={`grid grid-cols-2 gap-1.5 ${formData.timeUnknown ? 'opacity-40 pointer-events-none' : ''}`}>
          <MiniSelect value={formData.hour} onChange={(v) => editForm(p => ({ ...p, hour: v }))}
            options={HOURS} placeholder="Час" formatOption={(h) => String(h).padStart(2, '0')} maxLength={2} testId="natal-birth-hour" />
          <MiniSelect value={formData.minute} onChange={(v) => editForm(p => ({ ...p, minute: v }))}
            options={MINUTES} placeholder="Мин" formatOption={(m) => String(m).padStart(2, '0')} maxLength={2} testId="natal-birth-minute" />
        </div>
        {/* «Не знаю время рождения». Главная дверь в калькулятор для тех,
            кто строит карту ДРУГОГО человека: время мужа, сына, коллеги
            почти никогда не знают, и форма без этой галочки их разворачивала.
            Не <Checkbox> из ui: вся строка — одна зона нажатия, на телефоне
            в квадратик 14px пальцем попадают не всегда. */}
        <label
          className="mt-1.5 flex items-start gap-1.5 cursor-pointer select-none"
          data-testid="natal-time-unknown-label"
        >
          <input
            type="checkbox"
            checked={!!formData.timeUnknown}
            onChange={(e) => editForm(p => ({ ...p, timeUnknown: e.target.checked }))}
            className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-sky-600 cursor-pointer"
            data-testid="natal-time-unknown"
          />
          <span className="text-[11px] leading-snug text-slate-600">Не знаю время рождения</span>
        </label>
        {formData.timeUnknown && (
          <p className="mt-1 text-[10px] leading-snug text-slate-500" data-testid="natal-time-unknown-note">
            Построим карту на 12:00 — планеты в знаках будут верными. Луну, Асцендент и дома без времени рождения не учитываем
          </p>
        )}
      </div>
      <div className="relative" ref={suggestionsRef}>
        <Label className="flex items-center gap-1 text-slate-600 text-[11px] font-medium mb-1">
          <MapPin className="w-3 h-3 text-slate-400" /> Место рождения
        </Label>
        <div className="relative">
          <Input type="text" placeholder="Введите город..." value={citySearch}
            onChange={(e) => {
              setCitySearch(e.target.value);
              setShowSuggestions(e.target.value.trim().length >= 1);
              if (formData.city && e.target.value !== `${formData.city}, ${formData.country}`)
                editForm(p => ({ ...p, city: '', country: '', latitude: '', longitude: '', timezone: '' }));
            }}
            onFocus={() => { if (citySuggestions.length > 0) setShowSuggestions(true); }}
            className="h-8 bg-white border-slate-200 focus:border-sky-500 text-slate-800 text-xs pr-8"
            autoComplete="off" data-testid="natal-city-search" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {isSearching ? <Loader2 className="w-3 h-3 text-slate-400 animate-spin" /> : <Search className="w-3 h-3 text-slate-400" />}
          </div>
        </div>
        {showSuggestions && !cityPicked && citySearch.trim().length >= 1 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
            {isSearching && (
              <div className="px-3 py-2 text-[11px] text-slate-400" data-testid="natal-city-searching">Ищем…</div>
            )}
            {!isSearching && citySuggestions.length === 0 && (
              <div className="px-3 py-2 text-[11px] text-slate-500" role="status" data-testid="natal-city-not-found">{CITY_NOT_FOUND_TEXT}</div>
            )}
            {citySuggestions.map((city, i) => (
              <button key={`${city.latitude}-${city.longitude}-${i}`} type="button"
                className="w-full px-3 py-1.5 text-left flex items-center gap-1.5 text-slate-600 hover:bg-sky-50 text-[11px]"
                onClick={() => handleCitySelect(city)} data-testid={`natal-city-${i}`}>
                <Globe className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span className="truncate">{city.displayName || `${city.city}, ${city.country}`}</span>
              </button>
            ))}
          </div>
        )}
        {formData.city && formData.latitude && (
          <p className="text-[10px] text-emerald-600 mt-0.5">{formData.city} ({Number(formData.latitude).toFixed(1)}, {Number(formData.longitude).toFixed(1)})</p>
        )}
      </div>
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button type="button" variant="ghost" className="w-full justify-between text-slate-400 hover:text-slate-600 p-0 h-6 text-[10px]">
            <span className="flex items-center gap-1"><Settings className="w-3 h-3" /> Координаты вручную</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-1.5 space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <Label className="text-[10px] text-slate-500">Широта</Label>
              <Input type="number" step="0.0001" placeholder="55.7558" value={formData.latitude}
                onChange={(e) => editForm(p => ({ ...p, latitude: e.target.value }))}
                className="h-7 text-[11px] bg-white border-slate-200" data-testid="natal-lat" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Долгота</Label>
              <Input type="number" step="0.0001" placeholder="37.6173" value={formData.longitude}
                onChange={(e) => editForm(p => ({ ...p, longitude: e.target.value }))}
                className="h-7 text-[11px] bg-white border-slate-200" data-testid="natal-lng" />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
      {error && (
        <div className="flex items-center gap-1 text-red-600 text-[11px] bg-red-50 rounded px-2 py-1.5">
          <AlertCircle className="w-3 h-3 flex-shrink-0" /> {error}
        </div>
      )}
      <Button type="submit" disabled={!isFormValid() || isLoading}
        className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 rounded-lg text-sm shadow-md shadow-sky-200/50 hover:shadow-lg hover:shadow-sky-300/50"
        data-testid="natal-calculate-btn">
        {isLoading ? (
          <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Расчёт...</span>
        ) : (
          <span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> РАССЧИТАТЬ</span>
        )}
      </Button>
      {previewHints.length > 0 && (
        <div className="space-y-1 pt-1" data-testid="natal-form-hints">
          {previewHints.map((h) => (
            <p key={h.kind} className="text-[11px] leading-snug text-slate-600" data-testid={`natal-hint-${h.kind}`}>
              <span className="font-semibold text-slate-800">{h.title}:</span> {h.text}
            </p>
          ))}
        </div>
      )}
    </form>
  );

  return { renderForm, chartData, birthParams, isLoading, isDemo, getUserTier, calcCount, previewChart, loadChart };
}
